package com.elan.edubridge

import com.elan.edubridge.data.firebase.installWebGraph
import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.ui.member.MemberRoutes
import com.elan.edubridge.ui.shell.AppHub
import com.elan.edubridge.ui.shell.AuthPage
import com.elan.edubridge.ui.shell.GuidePage
import com.elan.edubridge.ui.shell.GuardianConsentPendingPage
import com.elan.edubridge.ui.shell.InvitationLandingPage
import com.elan.edubridge.ui.shell.LandingPage
import com.elan.edubridge.ui.shell.LegalPages
import com.elan.edubridge.ui.shell.QrLandingPage
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.SessionStore
import com.elan.edubridge.ui.shell.publicOnly
import com.elan.edubridge.ui.shell.requireSession
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch

/**
 * 부트스트랩 셸. 실제 화면은 전부 `ui` 패키지 아래에 있고, 여기서는
 * ① 프로덕션 그래프 설치(Firebase) → ② 라우트 테이블 등록 → ③ 저장된 세션 복원
 * → ④ 세션이 바뀔 때마다 현재 경로 재디스패치만 한다.
 *
 * [installWebGraph]가 첫 줄인 이유: 실 서비스는 반드시 DB(Firestore) 기반으로 동작해야 하고,
 * 설치 실패가 데모 데이터(InMemory)로 조용히 폴백하는 경로가 있어서는 안 된다.
 * 실패하면 명시적 오류 화면을 그리고 멈춘다.
 */
private val scope = MainScope()
private val accountRepository by lazy { SharedGraph.createAccountRepository() }

fun main() {
    try {
        installWebGraph()
    } catch (t: Throwable) {
        console.error("EduBridge: Firebase 초기화 실패 — 데모 폴백 없이 중단한다", t)
        document.body?.innerHTML =
            """<div style="max-width:32rem;margin:20vh auto;padding:2rem;font-family:Pretendard,sans-serif;text-align:center">
               <h1 style="font-size:1.25rem;color:var(--eb-primary-900)">서비스 초기화에 실패했습니다</h1>
               <p style="color:#65708A">잠시 후 새로고침해 주세요. 문제가 계속되면 고객지원으로 연락해 주세요.</p>
               </div>"""
        return
    }

    registerRoutes()

    scope.launch {
        // 저장된 인증 상태를 먼저 복원해야 첫 렌더가 올바른 화면을 그린다(새로고침마다 로그인으로 튕기는 것 방지).
        accountRepository.restoreSession()
        accountRepository.session.collect { session ->
            SessionStore.current = session
            Router.start()
        }
    }
}

private fun registerRoutes() {
    Router.register("/", publicOnly { LandingPage.render() })
    Router.register("/login", publicOnly { AuthPage.renderLogin() })
    Router.register("/signup", publicOnly { AuthPage.renderSignup() })
    Router.register("/qr") { QrLandingPage.render() }
    Router.register("/invite") { InvitationLandingPage.render() }
    Router.register("/guide") { GuidePage.render() }

    Router.register("/terms") { LegalPages.render(LegalPages.Page.TERMS) }
    Router.register("/privacy") { LegalPages.render(LegalPages.Page.PRIVACY) }
    Router.register("/support") { LegalPages.render(LegalPages.Page.SUPPORT) }
    Router.register("/account-deletion") { LegalPages.render(LegalPages.Page.ACCOUNT_DELETION) }

    Router.register("/app/guardian-consent") {
        when {
            !SessionStore.current.isAuthenticated -> Router.replace("/login?redirect=/app/guardian-consent")
            !SessionStore.current.requiresGuardianConsent -> Router.replace("/app")
            else -> GuardianConsentPendingPage.render()
        }
    }
    Router.register("/app", requireSession { session, _ -> Router.replace(AppHub.resolveRedirect(session)) })
    // 플랫폼 관리자 리터럴 라우트는 아래의 `{academyId}/{section}` 동적 라우트보다 먼저 등록한다
    // (Router는 첫 매치 승리 — 세그먼트 개수가 같은 라우트끼리만 충돌 가능, 자세한 근거는 Router.kt 참고).
    Router.register("/app/admin", requireSession { session, _ -> AppHub.renderAdminDashboard(session) })
    Router.register("/app/admin/applications", requireSession { session, _ -> AppHub.renderAdminApplications(session) })
    // 회원 뷰(/app/me + 하위)는 lane D 구현이 정본이다 — 와일드카드보다 반드시 먼저.
    MemberRoutes.register()
    // 4세그먼트라 위 3세그먼트 패턴들과는 애초에 충돌하지 않는다(등록 순서 무관).
    Router.register("/app/{academyId}/qr/kiosk", requireSession { session, ctx ->
        AppHub.renderKiosk(session, ctx.params.getValue("academyId"))
    })
    Router.register("/app/{academyId}/{section}", requireSession { session, ctx ->
        AppHub.renderStaffConsole(session, ctx.params.getValue("academyId"), ctx.params.getValue("section"))
    })

    Router.setNotFound { Router.replace(if (SessionStore.current.isAuthenticated) "/app" else "/") }
}
